import { Shape, Vector2 } from 'three'

/**
 * Creates a 2D shape for an I or H steel profile cross-section
 * @param width - Overall width of the profile
 * @param height - Overall height of the profile
 * @param thicknessFlange - Thickness of the top and bottom flanges
 * @param thicknessWeb - Thickness of the vertical web
 * @param radius - Fillet radius between web and flange (optional)
 */
export const createIHProfileShape = (
  width: number,
  height: number,
  thicknessFlange: number,
  thicknessWeb: number,
  radius = 0,
): Shape => {
  const halfWidth = width / 2
  const halfThicknessWeb = thicknessWeb / 2

  const points: Vector2[] = []

  // Starting from bottom-left, going counter-clockwise
  // Bottom flange - left side
  points.push(new Vector2(-halfWidth, -height / 2))
  points.push(new Vector2(-halfWidth, -height / 2 + thicknessFlange))

  // Left side of web (with optional radius)
  if (radius > 0) {
    // Simplified: just add the point without actual arc
    points.push(new Vector2(-halfThicknessWeb, -height / 2 + thicknessFlange))
  } else {
    points.push(new Vector2(-halfThicknessWeb, -height / 2 + thicknessFlange))
  }

  // Left side of web going up
  points.push(new Vector2(-halfThicknessWeb, height / 2 - thicknessFlange))

  // Top flange - left side
  if (radius > 0) {
    points.push(new Vector2(-halfThicknessWeb, height / 2 - thicknessFlange))
  }
  points.push(new Vector2(-halfWidth, height / 2 - thicknessFlange))
  points.push(new Vector2(-halfWidth, height / 2))

  // Top flange - right side
  points.push(new Vector2(halfWidth, height / 2))
  points.push(new Vector2(halfWidth, height / 2 - thicknessFlange))

  // Right side of web going down
  points.push(new Vector2(halfThicknessWeb, height / 2 - thicknessFlange))
  points.push(new Vector2(halfThicknessWeb, -height / 2 + thicknessFlange))

  // Bottom flange - right side
  points.push(new Vector2(halfWidth, -height / 2 + thicknessFlange))
  points.push(new Vector2(halfWidth, -height / 2))

  return new Shape(points)
}

/**
 * Creates a 2D shape for a U steel profile cross-section (UPN, UPE)
 * @param width - Overall width of the profile
 * @param height - Overall height of the profile
 * @param thicknessFlange - Thickness of the flanges
 * @param thicknessWeb - Thickness of the web
 */
export const createUProfileShape = (
  width: number,
  height: number,
  thicknessFlange: number,
  thicknessWeb: number,
): Shape => {
  const points: Vector2[] = []

  // Starting from bottom-left, going counter-clockwise
  // Bottom-left outer corner
  points.push(new Vector2(-width / 2, -height / 2))
  // Bottom-left inner corner
  points.push(new Vector2(-width / 2 + thicknessFlange, -height / 2))
  points.push(new Vector2(-width / 2 + thicknessFlange, height / 2 - thicknessWeb))

  // Top-left inner corner
  points.push(new Vector2(-width / 2, height / 2 - thicknessWeb))
  points.push(new Vector2(-width / 2, height / 2))

  // Top edge
  points.push(new Vector2(width / 2, height / 2))

  // Top-right outer corner
  points.push(new Vector2(width / 2, height / 2 - thicknessWeb))
  points.push(new Vector2(width / 2 - thicknessFlange, height / 2 - thicknessWeb))

  // Bottom-right inner corner
  points.push(new Vector2(width / 2 - thicknessFlange, -height / 2))
  points.push(new Vector2(width / 2, -height / 2))

  return new Shape(points)
}

/**
 * Creates a 2D shape for a circular hollow steel profile (RO section)
 * @param diameter - Outer diameter
 * @param thickness - Wall thickness
 */
export const createCircularHollowShape = (diameter: number, thickness: number): Shape => {
  const outerRadius = diameter / 2
  const innerRadius = outerRadius - thickness

  // Create outer circle
  const outerShape = new Shape()
  outerShape.absarc(0, 0, outerRadius, 0, Math.PI * 2, false)

  // Create inner circle as a hole
  const innerHole = new Shape()
  innerHole.absarc(0, 0, innerRadius, 0, Math.PI * 2, true)

  outerShape.holes.push(innerHole)

  return outerShape
}

/**
 * Creates a 2D shape for a rectangular/square hollow steel profile (RRO/QRO section)
 * @param width - Overall width
 * @param height - Overall height
 * @param thickness - Wall thickness
 */
export const createRectangularHollowShape = (
  width: number,
  height: number,
  thickness: number,
): Shape => {
  const halfWidth = width / 2
  const halfHeight = height / 2

  // Outer rectangle
  const outerPoints: Vector2[] = [
    new Vector2(-halfWidth, -halfHeight),
    new Vector2(halfWidth, -halfHeight),
    new Vector2(halfWidth, halfHeight),
    new Vector2(-halfWidth, halfHeight),
  ]

  const outerShape = new Shape(outerPoints)

  // Inner rectangle (hole)
  const innerHalfWidth = halfWidth - thickness
  const innerHalfHeight = halfHeight - thickness

  const innerPoints: Vector2[] = [
    new Vector2(-innerHalfWidth, -innerHalfHeight),
    new Vector2(innerHalfWidth, -innerHalfHeight),
    new Vector2(innerHalfWidth, innerHalfHeight),
    new Vector2(-innerHalfWidth, innerHalfHeight),
  ]

  const innerHole = new Shape(innerPoints)
  outerShape.holes.push(innerHole)

  return outerShape
}

/**
 * Creates a rectangular profile shape (fallback for non-steel or missing profile data)
 */
export const createRectangularShape = (width: number, height: number): Shape => {
  const halfWidth = width / 2
  const halfHeight = height / 2

  const points: Vector2[] = [
    new Vector2(-halfWidth, -halfHeight),
    new Vector2(halfWidth, -halfHeight),
    new Vector2(halfWidth, halfHeight),
    new Vector2(-halfWidth, halfHeight),
  ]

  return new Shape(points)
}
